import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, FileSpreadsheet, Loader2, Upload } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  cashflowApi,
  type StatementEntryStatus,
  type StatementPreview,
} from '../../../api/cashflow';
import { StatementImportDialogProps } from '../types';

/**
 * Импорт выписки банка (ADR 0026): файл → предпросмотр со статусом каждой строки
 * → подтверждение. До подтверждения в базу ничего не пишется; повторный импорт
 * того же или пересекающегося файла дублей не создаёт.
 */

const STATUS_META: Record<StatementEntryStatus, { label: string; hint: string; className: string }> = {
  new: {
    label: 'Новое',
    hint: 'станет Поступлением',
    className: 'bg-emerald-500/10 text-emerald-500',
  },
  matched_manual: {
    label: 'Уже заведено вручную',
    hint: 'привяжется к ручной записи — дубля не будет',
    className: 'bg-blue-500/10 text-blue-500',
  },
  imported: {
    label: 'Уже импортировано',
    hint: 'эта проводка уже в базе — пропускаем',
    className: 'bg-blue-500/10 text-blue-500',
  },
  foreign: {
    label: 'Не от провайдера',
    hint: 'другая деятельность или P2P — не импортируется',
    className: 'bg-slate-500/10 text-slate-500',
  },
  debit: {
    label: 'Списание',
    hint: 'не импортируется',
    className: 'bg-slate-500/10 text-slate-500',
  },
};

const ORDER: StatementEntryStatus[] = ['new', 'matched_manual', 'imported', 'foreign', 'debit'];

export function StatementImportDialog({
  isDark,
  isOpen,
  onOpenChange,
  onImported,
  formatMoney,
}: StatementImportDialogProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<StatementPreview | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [showSkipped, setShowSkipped] = useState(false);

  useEffect(() => {
    if (isOpen) return;
    setFile(null);
    setPreview(null);
    setShowSkipped(false);
  }, [isOpen]);

  const handleFile = async (picked: File | undefined) => {
    if (!picked) return;
    setFile(picked);
    setPreview(null);
    setIsParsing(true);
    try {
      setPreview(await cashflowApi.previewStatement(picked));
    } catch (error: any) {
      setFile(null);
      toast.error(error.response?.data?.message || 'Не удалось прочитать выписку');
    } finally {
      setIsParsing(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const toCommit = (preview?.summary.new.count ?? 0) + (preview?.summary.matched_manual.count ?? 0);

  const handleImport = async () => {
    if (!file || isImporting) return;
    setIsImporting(true);
    try {
      const result = await cashflowApi.importStatement(file);
      const parts = [
        result.created && `создано ${result.created}`,
        result.linked && `привязано к ручным ${result.linked}`,
      ].filter(Boolean);
      toast.success(parts.length ? `Импорт выписки: ${parts.join(', ')}` : 'Новых поступлений нет');
      onImported();
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось импортировать выписку');
    } finally {
      setIsImporting(false);
    }
  };

  const visibleEntries = useMemo(() => {
    if (!preview) return [];
    const relevant = preview.entries.filter((e) => showSkipped || (e.status !== 'foreign' && e.status !== 'debit'));
    return [...relevant].sort((a, b) => ORDER.indexOf(a.status) - ORDER.indexOf(b.status) || a.date.localeCompare(b.date));
  }, [preview, showSkipped]);

  const muted = isDark ? 'text-slate-400' : 'text-slate-500';
  const panel = isDark ? 'bg-slate-800/50' : 'bg-slate-50';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !isImporting && onOpenChange(open)}>
      <DialogContent className={cn('max-w-4xl max-h-[90vh] overflow-y-auto', isDark ? 'bg-[#1A1A22] border-slate-700' : 'bg-white')}>
        <DialogHeader>
          <DialogTitle className={cn(isDark ? 'text-white' : 'text-[#111111]')}>Импорт выписки банка</DialogTitle>
          <DialogDescription className={muted}>
            Выписка Ипотека-банка в .xlsx. В Поступления попадают только зачисления от Click, Payme и
            Paylov; уже импортированные строки и записи, заведённые вручную, повторно не создаются.
          </DialogDescription>
        </DialogHeader>

        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />

        <div className="space-y-4 py-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={isParsing || isImporting}
            className={cn(
              'w-full flex items-center gap-3 rounded-xl border-2 border-dashed p-4 text-left transition-colors',
              isDark ? 'border-slate-700 hover:border-[#C9A227]' : 'border-slate-200 hover:border-[#C9A227]',
            )}
          >
            {isParsing ? (
              <Loader2 className="w-6 h-6 animate-spin text-[#C9A227]" />
            ) : file ? (
              <FileSpreadsheet className="w-6 h-6 text-[#C9A227]" />
            ) : (
              <Upload className="w-6 h-6 text-[#C9A227]" />
            )}
            <div>
              <p className={cn('text-sm font-medium', isDark ? 'text-white' : 'text-[#111111]')}>
                {file ? file.name : 'Выбрать файл выписки'}
              </p>
              <p className={cn('text-xs', muted)}>
                {isParsing ? 'Читаем выписку…' : file ? 'Нажмите, чтобы выбрать другой файл' : '.xlsx, до 5 МБ'}
              </p>
            </div>
          </button>

          {preview && (
            <>
              {preview.warnings.map((w) => (
                <div key={w} className="flex gap-2 rounded-xl bg-amber-500/10 p-3 text-sm text-amber-600">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{w}</span>
                </div>
              ))}

              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {ORDER.map((status) => {
                  const row = preview.summary[status];
                  const meta = STATUS_META[status];
                  return (
                    <div key={status} className={cn('rounded-xl p-3', panel)}>
                      <span className={cn('inline-block rounded-lg px-2 py-0.5 text-xs font-medium', meta.className)}>
                        {meta.label}
                      </span>
                      <p className={cn('text-xl font-bold mt-1', isDark ? 'text-white' : 'text-[#111111]')}>
                        {row.count}
                      </p>
                      <p className={cn('text-xs', muted)}>{formatMoney(row.sum)}</p>
                      <p className={cn('text-[11px] mt-1 leading-tight', muted)}>{meta.hint}</p>
                    </div>
                  );
                })}
              </div>

              <div className="flex items-center justify-between">
                <p className={cn('text-sm font-medium', isDark ? 'text-white' : 'text-[#111111]')}>Строки выписки</p>
                <label className={cn('flex items-center gap-2 text-xs cursor-pointer', muted)}>
                  <input type="checkbox" checked={showSkipped} onChange={(e) => setShowSkipped(e.target.checked)} />
                  Показать пропускаемые (чужие и списания)
                </label>
              </div>

              <div className="space-y-1.5">
                {visibleEntries.length === 0 && (
                  <p className={cn('text-sm text-center py-6', muted)}>Зачислений от провайдеров в файле нет.</p>
                )}
                {visibleEntries.map((e) => {
                  const meta = STATUS_META[e.status];
                  return (
                    <div key={`${e.fingerprint}-${e.status}`} className={cn('flex items-start justify-between gap-4 rounded-xl px-3 py-2', panel)}>
                      <div className="min-w-0">
                        <p className={cn('text-sm', isDark ? 'text-white' : 'text-[#111111]')}>
                          <span className="font-medium">{e.providerName ?? e.senderName}</span>
                          <span className={cn('ml-2 text-xs', muted)}>
                            {e.date} {e.time} · №{e.document}
                          </span>
                        </p>
                        <p className={cn('text-xs truncate', muted)} title={e.purpose}>
                          {e.purpose || '—'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={cn('text-sm font-semibold', e.status === 'debit' ? 'text-red-500' : isDark ? 'text-white' : 'text-[#111111]')}>
                          {formatMoney(e.status === 'debit' ? -e.debit : e.credit)}
                        </span>
                        <span className={cn('rounded-lg px-2 py-0.5 text-xs font-medium', meta.className)}>{meta.label}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isImporting}
            className={cn(isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-200')}
          >
            Отмена
          </Button>
          <Button
            onClick={handleImport}
            disabled={!preview || toCommit === 0 || isImporting}
            className="bg-[#C9A227] hover:bg-[#C9A227]/90"
          >
            {isImporting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {preview && toCommit === 0
              ? 'Нечего импортировать'
              : `Импортировать ${preview?.summary.new.count ?? 0} новых` +
                (preview?.summary.matched_manual.count ? ` и привязать ${preview.summary.matched_manual.count}` : '')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
